import * as React from 'react';
import type { WidgetData, WidgetType } from './types';
import { Bars, Gauge, Sparkline, Provenance, fmt } from './widgets/charts';
import { Stat } from './widgets/stat';
import { Table } from './widgets/table';
import { getWidgetDataProvider, type WidgetLiveValues } from './live';

/**
 * The one registry both editor lanes render through (doc-widgets README §D2/D3).
 * "Identical in both layouts" is not a review item — it is this file being the only implementation.
 */

export interface WidgetDef {
  label: string;
  keywords: string;
  /** Structural validation. Returns null when usable, else a human-readable reason. */
  validate: (data: WidgetData) => string | null;
  /** A visibly-sample payload for insert menus. Sample data must never look like a measurement. */
  samplePayload: () => WidgetData;
}

function requireSeries(data: WidgetData): string | null {
  if (!Array.isArray(data.series) || !data.series.length) {
    return 'series is required';
  }
  return null;
}

function requireProvenance(data: WidgetData): string | null {
  if (!data.source?.runId || !data.source?.nodeId) {
    return 'source {runId, nodeId} is required — a figure that cannot say who measured it is an assertion';
  }
  return null;
}

const chartValidate = (data: WidgetData) => requireSeries(data) ?? requireProvenance(data);

const sampleSource = { runId: 'sample-run', nodeId: 'sample' };

export const WIDGET_REGISTRY: Record<WidgetType, WidgetDef> = {
  'chart.bars': {
    label: 'Bar chart',
    keywords: 'widget chart bars edges compare',
    validate: chartValidate,
    samplePayload: () => ({
      type: 'chart.bars',
      title: 'Sample bars — replace with recorded values',
      source: sampleSource,
      series: [{
        name: 'sample',
        unit: 'pts',
        points: [
          { label: 'sample A', value: 12.5 },
          { label: 'sample B (dormant)', value: -7.2, state: 'dormant' },
          { label: 'sample C (not recorded)', value: null },
        ],
      }],
      caption: 'Sample data — every value here must be replaced with a recorded one.',
    }),
  },
  'chart.gauge': {
    label: 'Gauge',
    keywords: 'widget gauge confidence percentile',
    validate: chartValidate,
    samplePayload: () => ({
      type: 'chart.gauge',
      title: 'Sample gauge — replace with recorded values',
      source: sampleSource,
      series: [{
        name: 'sample',
        points: [
          { label: 'sample confidence', value: 0.42 },
          { label: 'sample (not recorded)', value: null },
        ],
      }],
      caption: 'Sample data — 0..1 confidences and percentiles.',
    }),
  },
  'chart.sparkline': {
    label: 'Sparkline',
    keywords: 'widget sparkline series trend line',
    validate: chartValidate,
    samplePayload: () => ({
      type: 'chart.sparkline',
      title: 'Sample sparkline — replace with recorded values',
      source: sampleSource,
      series: [{
        name: 'sample',
        points: [
          { label: 't1', value: 3 }, { label: 't2', value: 5 }, { label: 't3', value: null },
          { label: 't4', value: 4 }, { label: 't5', value: 6 },
        ],
      }],
      caption: 'Sample data — the null at t3 renders as a gap, never bridged.',
    }),
  },
  'chart.provenance': {
    label: 'Provenance weights',
    keywords: 'widget provenance evidence weight edges',
    validate: chartValidate,
    samplePayload: () => ({
      type: 'chart.provenance',
      title: 'Sample provenance — replace with recorded weights',
      source: sampleSource,
      series: [{
        name: 'sample',
        points: [
          { label: 'sample claim → E1', value: 0.8 },
          { label: 'sample claim → E2 (not recorded)', value: null },
        ],
      }],
      caption: 'Sample data — thickness comes from recorded weight inputs only.',
    }),
  },
  stat: {
    label: 'Stat tile',
    keywords: 'widget stat kpi number metric tile',
    validate: (data) => (data.stat && data.stat.label ? null : 'stat {label, value} is required'),
    samplePayload: () => ({
      type: 'stat',
      title: 'Sample stat',
      source: sampleSource,
      stat: { label: 'sample metric', value: 1234, unit: '', delta: 56, deltaBasis: 'vs sample baseline' },
      caption: 'Sample data — a delta only colors when its basis is stated.',
    }),
  },
  table: {
    label: 'Data table',
    keywords: 'widget table rows columns data',
    validate: (data) =>
      data.table &&
      Array.isArray(data.table.columns) &&
      Array.isArray(data.table.rows) &&
      data.table.rows.every((row) => Array.isArray(row))
        ? null
        : 'table {columns, rows: cell[][]} is required',
    samplePayload: () => ({
      type: 'table',
      title: 'Sample table — replace with recorded rows',
      source: sampleSource,
      table: {
        columns: ['sample item', 'value', 'note'],
        rows: [['sample A', 42, 'recorded'], ['sample B', null, 'not recorded']],
      },
      caption: 'Sample data.',
    }),
  },
};

export function deriveAlt(data: WidgetData): string {
  if (data.alt && data.alt.trim()) {
    return data.alt;
  }
  const bits = [data.title];
  if (data.caption) {
    bits.push(data.caption);
  }
  if (data.source?.nodeId) {
    bits.push(`from ${data.source.nodeId}`);
  }
  return bits.join(' — ');
}

/** Overlay live values onto a copy of the series — the stored data object is untouched. */
function withLiveOverlay(data: WidgetData, live: WidgetLiveValues): WidgetData {
  const series = (data.series ?? []).map((s) => ({
    ...s,
    points: s.points.map((p) =>
      p.label in live.values ? { ...p, value: live.values[p.label] } : p,
    ),
  }));
  const stat =
    data.stat && data.stat.label in live.values
      ? { ...data.stat, value: live.values[data.stat.label] }
      : data.stat;
  return { ...data, series, stat };
}

/**
 * Coerce a payload that may have been stringified in transit (doc-widgets, found live 2026-08-04).
 *
 * The v1 editor is multiplayer: node attrs round-trip through Yjs, whose XML attributes stringify
 * object values — so a chart/widget node whose `data` was stored as an object comes back as a JSON
 * STRING after a collab round-trip, `.kind`/`.type` read undefined, and every figure rendered
 * "Unknown widget type \"chart.undefined\"" while the stored document was perfectly valid.
 * Tolerating the string at the ONE shared entry point fixes every host at once.
 */
export function coerceWidgetData(raw: unknown): Record<string, unknown> | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

/**
 * Adapt a legacy `chart` node payload ({kind, ...}) to the registry shape ({type: "chart.<kind>"}).
 * Lives HERE so the v1 and v2 hosts share one implementation — including the string coercion above.
 */
export function adaptLegacyChart(raw: unknown): WidgetData | null {
  const obj = coerceWidgetData(raw);
  if (!obj) {
    return null;
  }
  const kind = typeof obj.kind === 'string' ? obj.kind : null;
  if (!kind) {
    return obj as unknown as WidgetData;
  }
  return { ...obj, type: `chart.${kind}` } as unknown as WidgetData;
}

/**
 * The single render entry point. Malformed data renders its reason and the alt text — a broken
 * widget must cost the reader the figure, never the document, and must stay diagnosable.
 */
export function WidgetView({ data: rawData }: { data: WidgetData | string | null }) {
  const data = coerceWidgetData(rawData) as unknown as WidgetData | null;
  const [live, setLive] = React.useState<WidgetLiveValues | null>(null);
  const [busy, setBusy] = React.useState(false);

  if (!data || !data.type || !(data.type in WIDGET_REGISTRY)) {
    return (
      <div className="docit-widget docit-widget-broken" style={frame}>
        <em style={{ opacity: 0.7, fontSize: 12 }}>
          {data ? `Unknown widget type "${String((data as WidgetData).type)}" — ${deriveAlt(data)}` : 'Widget unavailable.'}
        </em>
      </div>
    );
  }

  const def = WIDGET_REGISTRY[data.type];
  const invalid = def.validate(data);
  if (invalid) {
    return (
      <div className="docit-widget docit-widget-broken" style={frame}>
        <em style={{ opacity: 0.7, fontSize: 12 }}>Widget "{data.title}" not rendered: {invalid}</em>
      </div>
    );
  }

  const provider = data.sourceRef ? getWidgetDataProvider(data.sourceRef.kind) : undefined;
  const shown = live ? withLiveOverlay(data, live) : data;

  const body =
    shown.type === 'stat' ? <Stat stat={shown.stat!} />
    : shown.type === 'table' ? <Table table={shown.table!} />
    : shown.type === 'chart.bars' ? <Bars series={shown.series!} />
    : shown.type === 'chart.gauge' ? <Gauge series={shown.series!} />
    : shown.type === 'chart.sparkline' ? <Sparkline series={shown.series!} />
    : <Provenance series={shown.series!} />;

  return (
    <div className="docit-widget" style={frame}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={titleStyle}>{data.title}</div>
        {provider && (
          <button
            type="button"
            className="docit-widget-refresh"
            style={{ fontSize: 10, cursor: 'pointer' }}
            disabled={busy}
            onClick={() => {
              setBusy(true);
              provider(data.sourceRef!, data)
                .then(setLive)
                .catch(() => setLive(null))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'refreshing…' : live ? 'refresh again' : 'refresh'}
          </button>
        )}
      </div>
      {live && (
        <div style={{ fontSize: 10, color: '#8a6d1a', margin: '2px 0 4px' }}>
          ⚡ live values as of {live.asOf} — stored values are the record; this is a view
        </div>
      )}
      {body}
      <div style={footStyle}>
        {data.caption ? <span>{data.caption}</span> : <span />}
        {data.source?.nodeId ? <span style={{ whiteSpace: 'nowrap' }}>measured by {data.source.nodeId}</span> : null}
      </div>
    </div>
  );
}

const frame: React.CSSProperties = {
  margin: '16px 0',
  padding: '12px 14px',
  border: '1px solid rgba(128,128,128,0.3)',
  borderRadius: 6,
  background: 'rgba(128,128,128,0.06)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  opacity: 0.75,
  marginBottom: 8,
};

const footStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  marginTop: 6,
  fontSize: 11,
  opacity: 0.65,
};

export { fmt };
